import { useEffect, useState } from "react";
import { Trophy, Medal, Loader2, Users, Target } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useI18n } from "../i18n";

export default function TournamentSummary() {
    const { t } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFinished, setNotFinished] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/tournament/summary");
                setData(data);
            } catch (e) {
                if (e.response?.status === 404) setNotFinished(true);
                else toast.error(formatApiError(e.response?.data?.detail) || e.message);
            } finally { setLoading(false); }
        })();
    }, []);

    if (loading) {
        return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[var(--accent-hex)]" /></div>;
    }
    if (notFinished) {
        return (
            <div className="max-w-2xl mx-auto p-8 text-center" data-testid="summary-pending">
                <Trophy className="h-12 w-12 text-[var(--text-muted)] mx-auto mb-4" />
                <h1 className="font-heading text-2xl uppercase font-extrabold text-[var(--text-primary)] mb-2">{t("summary.pending.title")}</h1>
                <p className="text-sm text-[var(--text-secondary)]">{t("summary.pending.body")}</p>
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="summary-page">
            <header>
                <div className="flex items-center gap-2 text-[var(--accent-hex)] mb-2">
                    <Trophy className="h-5 w-5" />
                    <span className="text-[10px] uppercase tracking-widest font-extrabold">{t("summary.title.eyebrow")}</span>
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase text-[var(--text-primary)]">{t("summary.title")}</h1>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                    {t("summary.subtitle", { date: data.tournament_finished_at ? new Date(data.tournament_finished_at).toLocaleString() : "—" })}
                </p>
            </header>

            {data.pools.map((entry) => (
                <section key={entry.pool.id} className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-5 space-y-4" data-testid={`summary-pool-${entry.pool.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <h2 className="font-heading text-xl font-extrabold uppercase text-[var(--text-primary)] flex items-center gap-2">
                            <Users className="h-4 w-4 text-[var(--accent-hex)]" /> {entry.pool.name}
                        </h2>
                        {entry.pool.bet_amount != null && (
                            <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                                Pot {entry.pool.leaderboard.length * entry.pool.bet_amount} {entry.pool.bet_currency}
                            </span>
                        )}
                    </div>

                    {/* Leaderboard */}
                    <div>
                        <h3 className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-2">{t("summary.leaderboard")}</h3>
                        <ol className="space-y-1">
                            {entry.leaderboard.slice(0, 10).map((row) => (
                                <li key={row.user_id} className="flex items-center justify-between p-2 rounded bg-[var(--bg-base)] border border-[var(--border-muted)]" data-testid={`summary-lb-${row.user_id}`}>
                                    <div className="flex items-center gap-2">
                                        <span className={`font-mono font-extrabold w-8 text-right tabular-nums ${row.rank <= 3 ? "text-amber-400" : "text-[var(--text-muted)]"}`}>#{row.rank}</span>
                                        <span className="text-sm font-bold text-[var(--text-primary)]">{row.user_name}</span>
                                        {row.rank === 1 && <Medal className="h-3.5 w-3.5 text-amber-400" />}
                                    </div>
                                    <span className="font-heading font-extrabold text-[var(--accent-hex)] tabular-nums">{row.total_points} p</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    {/* Bonus predictions */}
                    <div>
                        <h3 className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-2 flex items-center gap-1">
                            <Target className="h-3 w-3" /> {t("summary.bonus")}
                        </h3>
                        {/* Official answers row */}
                        <div className="text-[11px] mb-2 p-2 rounded bg-[var(--accent-hex)]/10 border border-[var(--accent-border)]">
                            <span className="uppercase tracking-widest font-bold text-[var(--accent-hex)]">{t("summary.official")}: </span>
                            <span className="text-[var(--text-primary)]">
                                {t("bonus.row.top_scorer.label")}: <strong>{entry.official_answers.top_scorer || "—"}</strong> ·
                                🥇 {entry.official_answers.gold || "—"} ·
                                🥈 {entry.official_answers.silver || "—"} ·
                                🥉 {entry.official_answers.bronze || "—"}
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs" data-testid={`summary-bonus-table-${entry.pool.id}`}>
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] border-b border-[var(--border-muted)]">
                                        <th className="text-left py-1.5 pr-3">{t("summary.member")}</th>
                                        <th className="text-left py-1.5 pr-3">{t("bonus.row.top_scorer.label")}</th>
                                        <th className="text-left py-1.5 pr-3">🥇</th>
                                        <th className="text-left py-1.5 pr-3">🥈</th>
                                        <th className="text-left py-1.5 pr-3">🥉</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entry.bonus_predictions.map((b) => (
                                        <tr key={b.user_id} className="border-b border-[var(--border-muted)]/40" data-testid={`summary-bonus-${b.user_id}`}>
                                            <td className="py-1.5 pr-3 font-bold text-[var(--text-primary)]">{b.user_name}</td>
                                            <td className="py-1.5 pr-3">{b.top_scorer || "—"}</td>
                                            <td className="py-1.5 pr-3">{b.gold || "—"}</td>
                                            <td className="py-1.5 pr-3">{b.silver || "—"}</td>
                                            <td className="py-1.5 pr-3">{b.bronze || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            ))}
        </div>
    );
}
